import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bot, Loader2, RotateCcw, User, X } from 'lucide-react';

import { listDashboardVersions } from '../../services/dashboardApi';

const when = (iso) =>
  new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Saved versions, newest first; Restore saves an old one again as the newest. */
const HistoryPanel = ({
  accessToken,
  dashboardId,
  currentVersion,
  canRestore,
  onRestore,
  onClose,
}) => {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listDashboardVersions(accessToken, dashboardId)
      .then((res) => !cancelled && setVersions(res?.data?.versions || []))
      .catch((e) => !cancelled && setError(e?.message || String(e)));
    return () => {
      cancelled = true;
    };
  }, [accessToken, dashboardId, currentVersion]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const restore = async (version) => {
    setBusy(version);
    try {
      await onRestore(version);
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50">
      <aside
        role="dialog"
        aria-label="Dashboard history"
        className="flex h-full w-full max-w-sm flex-col border-l border-zinc-700 bg-zinc-900 text-zinc-200"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-sm font-semibold">History</h2>
          <button onClick={onClose} title="Close" className="p-1 text-zinc-400">
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {error && <p className="p-2 text-xs text-amber-300">{error}</p>}
          {!error && !versions && (
            <Loader2 className="m-4 animate-spin text-zinc-500" />
          )}
          {versions?.map((v) => {
            const current = v.version === currentVersion;
            const Icon = v.author === 'ai' ? Bot : User;
            return (
              <div
                key={v.version}
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-zinc-800/50"
              >
                <Icon
                  size={14}
                  className="shrink-0 text-zinc-500"
                  aria-label={
                    v.author === 'ai' ? 'Saved by AI' : 'Saved by you'
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-zinc-200">
                    v{v.version} · {v.spec?.title}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {when(v.created_at)} · {v.spec?.widgets?.length || 0}{' '}
                    widgets
                  </p>
                </div>
                {current ? (
                  <span className="text-[11px] text-zinc-500">Current</span>
                ) : (
                  canRestore && (
                    <button
                      onClick={() => restore(v.version)}
                      disabled={busy !== null}
                      className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[11px] hover:bg-zinc-800 disabled:opacity-50"
                    >
                      {busy === v.version ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <RotateCcw size={12} aria-hidden="true" />
                      )}
                      Restore
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default HistoryPanel;
